import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import { defaultFills } from './Colors';

import styles from './EditableText.module.css';
import { Icon } from './Icon';

export interface IEditableTextProps extends BoxProps<'div'> {
  onConfirm?: (value: string, number?: number) => void;
  defaultValue?: string | number;
  type?: string;
  value?: string | number | null;
  unit?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  autoHeight?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  size?: number;
  autoFocus?: boolean;
}

export const EditableText: React.FC<IEditableTextProps> = ({
  onConfirm,
  type = 'text',
  value,
  defaultValue = value,
  unit,
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  autoHeight = false,
  placeholder,
  readOnly = false,
  maxLength,
  size,
  autoFocus = false,
  ...props
}) => {
  const intl = useIntl();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const parseLocaleNumber = useCallback(
    (str: string) => {
      // Detect the user's locale decimal separator:
      const decimalSeparator = intl.formatNumber(1.1).substring(1, 2);
      // Detect the user's locale thousand separator:
      let thousandSeparator = intl.formatNumber(1000).substring(1, 2);
      // In case there are locales that don't use a thousand separator
      if (thousandSeparator.match(/\d/)) thousandSeparator = '';

      const val = str
        .replaceAll(thousandSeparator, '')
        .replaceAll(decimalSeparator, '.');
      return parseFloat(val);
    },
    [intl]
  );
  const formatNumber = useCallback(
    (n: number) => {
      return intl.formatNumber(n, {
        style: 'decimal',
        maximumFractionDigits,
        minimumFractionDigits,
      });
    },
    [intl, maximumFractionDigits, minimumFractionDigits]
  );

  const [changed, setChanged] = React.useState(false);
  const [focussed, setFocussed] = useState(false);

  const [innerValue, setInnerValue] = React.useState(
    type === 'number' && defaultValue !== undefined && defaultValue !== null
      ? typeof defaultValue === 'number'
        ? formatNumber(defaultValue)
        : formatNumber(parseLocaleNumber(defaultValue))
      : defaultValue || ''
  );

  const resetValue = useCallback(() => {
    if (value === undefined) return;
    if (value === null) {
      setInnerValue('');
    } else {
      setInnerValue(
        type === 'number'
          ? typeof value === 'number'
            ? formatNumber(value)
            : formatNumber(parseLocaleNumber(value))
          : value
      );
    }
  }, [formatNumber, parseLocaleNumber, type, value]);

  useEffect(() => {
    resetValue();
  }, [formatNumber, parseLocaleNumber, resetValue, type, value]);

  useLayoutEffect(() => {
    // Resize textArea if it exists in dom/ref
    if (autoHeight && textAreaRef.current) {
      // Set timeout ensures that the textarea has a value

      const textArea = textAreaRef.current;
      textArea.style.cssText = 'height: auto;';
      textArea.style.cssText = `height: ${textArea.scrollHeight + 2}px;`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerValue, autoHeight, value, textAreaRef.current, focussed, changed]);

  const confirm = (val: string) => {
    if (type === 'number') {
      const n = parseLocaleNumber(val);
      changed && onConfirm && onConfirm(val, n);
    } else {
      changed && onConfirm && onConfirm(val, undefined);
    }
    setChanged(false);
  };

  const inputProps = {
    value: innerValue,
    placeholder,
    onFocus: (e: any) => {
      setFocussed(true);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocussed(false);
      if (type === 'number') {
        let n = 0;
        if (e.target.value) n = parseLocaleNumber(e.target.value);
        if (isNaN(n)) {
          setInnerValue(value || '');
        } else {
          setInnerValue(formatNumber(n));
        }
      }
      confirm(e.target.value || '0');
      // Return scroll to left-most
      e.target.scrollLeft = e.target.scrollWidth;
    },
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setChanged(true);
      setInnerValue(e.target.value);
    },
    onKeyDown: (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;
        target.blur();
      }
    },
  };

  return (
    <Box
      title={`${innerValue} ${unit ? unit : ''}`}
      lineHeight="24px"
      display="grid"
      gridTemplateColumns={unit ? '1fr auto' : '1fr'}
      alignItems="center"
      width="100%"
      position="relative"
      {...props}
    >
      {readOnly && focussed && (
        <Icon
          position="absolute"
          icon="lock"
          color={defaultFills.muted}
          size={10}
          left={4}
        />
      )}
      {type === 'number' ? (
        <input
          {...inputProps}
          autoFocus={autoFocus}
          readOnly={readOnly}
          type="text"
          className={styles.editableText}
          maxLength={maxLength}
          size={size}
        />
      ) : (
        <textarea
          {...inputProps}
          autoFocus={autoFocus}
          ref={textAreaRef}
          className={styles.editableText}
          rows={1}
          style={{
            resize: autoHeight ? undefined : 'none',
            width: '100%',
          }}
        />
      )}
      {unit && (
        <span
          style={{
            display: 'inline-block',
            minWidth: unit === '%' ? undefined : '26px',
            textAlign: 'left',
          }}
        >
          {unit}
        </span>
      )}
    </Box>
  );
};
